import { useState, useEffect, useCallback, type ReactNode } from "react";
import {
  Play, Square, Download, ExternalLink, ChevronLeft, ChevronRight,
  Users, Activity, Save, RotateCcw, AlertTriangle, Link as LinkIcon,
  LogOut, ArrowLeft, Plus, Trash2, Settings, Loader2, X, Shield, Check,
  Copy, ChevronUp, ChevronDown, MapPin, Pause, BarChart2, RefreshCw,
  FileText, Sparkles,
} from "lucide-react";
import { useEvent } from "../hooks/useEvent";
import { useResponses } from "../hooks/useResponses";
import { useAuth } from "../hooks/useAuth";
import { useAutoCluster } from "../hooks/useAutoCluster";
import { useLivePlacement } from "../hooks/useLivePlacement";
import { useClusters } from "../hooks/useClusters";
import { useEventTools } from "../hooks/useEventTools";
import { useCurrentEventRole } from "../hooks/useCurrentEventRole";
import { can } from "../lib/permissions";
import {
  setQuestionOpen, updateQuestion, exportEventLog, resetEvent, resetEventAI,
  setAIStatus, createQuestion, deleteQuestion, updateEvent,
  listStreams, createStream, updateStream, deleteStream, reorderStreams,
  setEventPaused, pauseRotation, getEventStats,
  triggerManifesto, getPendingManifestos, approveManifesto, setInsightDisplayed,
  setQuestionFrozen, triggerAI, assignResponseToCluster,
  getResponses, updateQuestionConfig, runEventAnalysis,
} from "../lib/api";
import { isVisibleResponse } from "../lib/responses";
import ResponseModerator from "../components/ResponseModerator";
import AIPanel from "../components/AIPanel";
import ToolsEditor from "../components/ToolsEditor";
import StageRemoteRow from "../components/Facilitator/StageRemoteRow";
import type { AIInsight, Event, EventRole, EventStats, EventTool, ModerationRules, Question, QuestionConfig, QuestionType, MCQConfig, NPSConfig, PolarConfig, LikertConfig, Response, SeedCluster, Stream } from "../lib/types";
import { STADTTEIL_SLUGS, STADTTEIL_DISPLAY_NAMES } from "../lib/stadtteilSlugs";
import SeedClustersEditor from "../components/SeedClustersEditor";
import { QuestionTypePicker } from "../components/Facilitator/QuestionTypePicker";
import { NPSConfigForm, DEFAULT_NPS_CONFIG } from "../components/Facilitator/NPSConfigForm";
import { MCQConfigForm, DEFAULT_MCQ_CONFIG } from "../components/Facilitator/MCQConfigForm";
import { PolarConfigForm, DEFAULT_POLAR_CONFIG } from "../components/Facilitator/PolarConfigForm";
import { LikertConfigForm, DEFAULT_LIKERT_CONFIG } from "../components/Facilitator/LikertConfigForm";
import CoFacilitatorCard from "../components/Facilitator/CoFacilitatorCard";
import PresentationKeyCard from "../components/Facilitator/PresentationKeyCard";
import { isoToLocalInput, localInputToIso } from "../lib/surveyWindow";
import { isEligibleSegmentQuestion } from "../lib/stageViews";

interface Props {
  event: Event;
  accessToken: string;
  onBack: () => void;
}

type Tab = "live" | "ai" | "settings" | "event-settings" | "stats";

/** "ai" removes cluster runs, insights and the freeze; "all" additionally deletes every answer. */
type ResetMode = "ai" | "all";

const STATUS_LABEL: Record<Event["status"], string> = {
  active: "Live",
  waiting: "Vorbereitung",
  completed: "Abgeschlossen",
};

export default function FacilitatorView({ event: initialEvent, accessToken, onBack }: Props) {
  const { questions, activeQuestion, loading, reload } = useEvent(initialEvent.id);
  const { signOut } = useAuth();
  const [event, setEvent] = useState(initialEvent);
  const { role, loading: roleLoading } = useCurrentEventRole(event.id);
  const [selectedQIdx, setSelectedQIdx] = useState(0);
  const [tab, setTab] = useState<Tab>("live");
  const [settingsInitialTab, setSettingsInitialTab] = useState<SettingsTab>("grundlagen");
  const [exporting, setExporting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [pauseToast, setPauseToast] = useState<string | null>(null);
  const [showManifestoModal, setShowManifestoModal] = useState(false);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [showAnalysisConfirm, setShowAnalysisConfirm] = useState(false);

  const selectedQuestion = questions[selectedQIdx] ?? null;
  const { responses } = useResponses(selectedQuestion?.id ?? null);
  const { tools } = useEventTools(event.id);
  const isSurvey = event.event_mode === "survey";

  // Once the facilitator has manually clustered an active question, keep the
  // clusters fresh by re-running cluster (not insights) every N new visible
  // responses. Insights remain intentional, facilitator-triggered moments.
  // Survey mode is handled separately by useSurveyAI on the stage view.
  useAutoCluster({
    question: !isSurvey ? activeQuestion : null,
    accessToken,
    enabled: !isSurvey,
    reclusterThreshold: event.recluster_threshold,
  });

  useLivePlacement(activeQuestion?.id ?? null);

  useEffect(() => {
    if (activeQuestion && !isSurvey) {
      const idx = questions.findIndex((q) => q.id === activeQuestion.id);
      if (idx >= 0) setSelectedQIdx(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestion?.id]);

  useEffect(() => {
    if (role === "co_mod" && (tab === "event-settings" || tab === "settings")) {
      setTab("live");
    }
  }, [role, tab]);

  const handleOpenQuestion = async (q: Question) => {
    for (const other of questions) {
      if (other.is_open && other.id !== q.id) await setQuestionOpen(other.id, false);
    }
    await setQuestionOpen(q.id, true);
  };

  const handleCloseQuestion = async (q: Question) => {
    await setQuestionOpen(q.id, false);
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;
    if (!window.confirm("Diese Frage löschen? Alle Antworten und Insights werden dauerhaft entfernt.")) return;
    await deleteQuestion(selectedQuestion.id);
    setSelectedQIdx(Math.max(0, selectedQIdx - 1));
    await reload();
  };

  const handleReset = async (mode: ResetMode) => {
    setResetting(true);
    try {
      if (mode === "ai") {
        await resetEventAI(event.id);
        setShowResetConfirm(false);
      } else {
        await resetEvent(event.id);
        setShowResetConfirm(false);
        setSelectedQIdx(0);
        setTab("live");
      }
    } finally {
      setResetting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const log = await exportEventLog(event.id);
      const blob = new Blob([JSON.stringify(log, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `readycal-event-${event.join_code}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleTogglePause = async () => {
    const next = !event.responses_paused;
    await setEventPaused(event.id, next);
    setEvent((e) => ({ ...e, responses_paused: next }));
    const msg = next ? "Stream pausiert." : "Stream fortgesetzt.";
    setPauseToast(msg);
    setTimeout(() => setPauseToast(null), 2500);
  };

  const handleSkipQuestion = async (direction: "prev" | "next") => {
    if (!isSurvey) return;
    const total = questions.length;
    if (total <= 1) return;
    const current = event.displayed_question_index ?? 0;
    const target = direction === "next"
      ? Math.min(current + 1, total - 1)
      : Math.max(current - 1, 0);
    if (target === current) return;
    await updateEvent(event.id, { displayed_question_index: target });
    setEvent((e) => ({ ...e, displayed_question_index: target }));
  };

  const handleToggleRotationPause = async () => {
    if (!isSurvey) return;
    const paused = event.rotation_interval_seconds == null;
    await pauseRotation(event.id, !paused);
    // Optimistic local update — the realtime subscription on useEvent will
    // reconcile shortly after.
    setEvent((e) => ({
      ...e,
      rotation_interval_seconds: paused ? (e.rotation_interval_seconds_saved ?? null) : null,
      rotation_interval_seconds_saved: paused ? null : e.rotation_interval_seconds,
    }));
  };

  const handleRunAnalysis = async () => {
    if (analysisRunning) return;
    setAnalysisRunning(true);
    try {
      const result = await runEventAnalysis({
        eventId: event.id,
        questions,
        tools,
        accessToken,
      });
      console.info("[analysis]", result);
      await reload();
      setShowAnalysisConfirm(false);
      const parts: string[] = [];
      if (result.clustered > 0) parts.push(`${result.clustered} ${result.clustered === 1 ? "Frage" : "Fragen"} geclustert`);
      if (result.insights > 0) parts.push(`${result.insights} Insights`);
      if (result.meta > 0) parts.push(`${result.meta} ${result.meta === 1 ? "Meta-Analyse" : "Meta-Analysen"}`);
      if (result.manifesto) parts.push("Manifest erstellt (Freigabe im Manifest-Dialog)");
      if (result.skipped > 0) parts.push(`${result.skipped} übersprungen (weniger als 2 sichtbare Antworten)`);
      setPauseToast(parts.length > 0 ? `Auswertung abgeschlossen: ${parts.join(" · ")}.` : "Auswertung abgeschlossen.");
      setTimeout(() => setPauseToast(null), 6000);
    } catch (err) {
      console.error("[analysis] failed", err);
      setPauseToast("Auswertung fehlgeschlagen. Bitte erneut versuchen.");
      setTimeout(() => setPauseToast(null), 4000);
    } finally {
      setAnalysisRunning(false);
    }
  };

  useEffect(() => {
    if (!roleLoading && role === null) {
      onBack();
    }
  }, [roleLoading, role, onBack]);

  if (loading) {
    return (
      <div className="min-h-screen bg-rc-cream flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-rc-berry animate-spin" />
      </div>
    );
  }

  const participantUrl = `${window.location.origin}/join/${event.join_code}`;
  const stageUrl = `${window.location.origin}/stage/${event.join_code}`;

  return (
    <div className="h-screen bg-rc-cream text-rc-black flex flex-col font-sans antialiased overflow-hidden">
      {pauseToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-rc-black text-rc-paper text-[13px] font-semibold rounded-pill px-5 py-2.5 shadow-hard animate-fade-up">
          {pauseToast}
        </div>
      )}
      <TopBar
        event={event}
        isSurvey={isSurvey}
        stageUrl={stageUrl}
        exporting={exporting}
        onBack={onBack}
        onExport={handleExport}
        onReset={() => setShowResetConfirm(true)}
        onSettings={() => { setSettingsInitialTab("grundlagen"); setTab("event-settings"); }}
        onStats={() => setTab("stats")}
        onManifesto={() => setShowManifestoModal(true)}
        onSignOut={signOut}
        onTogglePause={handleTogglePause}
        onToggleRotationPause={handleToggleRotationPause}
        displayedIdx={event.displayed_question_index ?? 0}
        totalQuestions={questions.length}
        onSkipQuestion={handleSkipQuestion}
        role={role}
        onRunAnalysis={can(role, "trigger_ai") ? () => setShowAnalysisConfirm(true) : undefined}
        analysisRunning={analysisRunning}
      />

      <div className="flex flex-1 min-h-0">
        <Sidebar
          questions={questions}
          selectedQIdx={selectedQIdx}
          onSelectQuestion={setSelectedQIdx}
          onAddQuestion={() => setShowAddQuestion(true)}
          isSurvey={isSurvey}
          participantUrl={participantUrl}
          joinCode={event.join_code}
          visibleResponses={responses.filter((r) => !r.is_hidden).length}
          status={event.status}
          canAddQuestion={can(role, "edit_questions")}
        />

        <main className="flex-1 flex flex-col min-h-0 overflow-hidden bg-rc-cream">
          {tab === "stats" ? (
            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-8">
              <div className="flex items-center justify-between mb-6 max-w-2xl">
                <h2 className="text-rc-black font-bold text-[24px] tracking-[-0.015em]">
                  Statistiken.
                </h2>
                <button
                  type="button"
                  onClick={() => setTab("live")}
                  className="text-rc-charcoal hover:text-rc-black text-[14px] font-semibold transition-colors"
                >
                  Zurück
                </button>
              </div>
              <EventStatsPanel eventId={event.id} aiBudgetUsd={event.ai_budget_usd ?? 15} />
            </div>
          ) : tab === "event-settings" ? (
            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-8">
              <div className="flex items-center justify-between mb-6 max-w-2xl">
                <h2 className="text-rc-black font-bold text-[24px] tracking-[-0.015em]">
                  Event-Einstellungen.
                </h2>
                <button
                  type="button"
                  onClick={() => setTab("live")}
                  className="text-rc-charcoal hover:text-rc-black text-[14px] font-semibold transition-colors"
                >
                  Zurück
                </button>
              </div>
              <EventSettingsEditor
                event={event}
                role={role}
                questions={questions}
                initialTab={settingsInitialTab}
                onSave={(updated) => setEvent(updated)}
              />
            </div>
          ) : selectedQuestion ? (
            <QuestionPane
              question={selectedQuestion}
              isSurvey={isSurvey}
              tab={tab}
              onTab={setTab}
              onOpen={handleOpenQuestion}
              onClose={handleCloseQuestion}
              onDelete={handleDeleteQuestion}
              responses={responses}
              accessToken={accessToken}
              event={event}
              tools={tools}
              onEditTools={can(role, "edit_tools") ? () => { setSettingsInitialTab("ki"); setTab("event-settings"); } : undefined}
              role={role}
              reload={reload}
            />
          ) : (
            <NoQuestionState onAdd={() => setShowAddQuestion(true)} />
          )}
        </main>
      </div>

      {showResetConfirm && (
        <ResetConfirmModal
          resetting={resetting}
          onCancel={() => setShowResetConfirm(false)}
          onConfirm={handleReset}
        />
      )}

      {showAnalysisConfirm && (
        <AnalysisConfirmModal
          running={analysisRunning}
          onCancel={() => setShowAnalysisConfirm(false)}
          onConfirm={handleRunAnalysis}
        />
      )}

      {showAddQuestion && (
        <AddQuestionModal
          nextIndex={questions.length + 1}
          onClose={() => setShowAddQuestion(false)}
          onCreate={async (text, hint, priming, questionType, questionConfig) => {
            await createQuestion(event.id, {
              text,
              hint,
              priming_text: priming,
              order_index: questions.length + 1,
              question_type: questionType,
              config: questionConfig,
            });
            await reload();
            setSelectedQIdx(questions.length);
            setShowAddQuestion(false);
          }}
        />
      )}

      {showManifestoModal && (
        <ManifestoModal
          eventId={event.id}
          joinCode={event.join_code}
          accessToken={accessToken}
          onClose={() => setShowManifestoModal(false)}
          canApprove={can(role, "approve_manifesto")}
        />
      )}
    </div>
  );
}

// ─── Top bar ──────────────────────────────────────────────────────────────

function TopBar({
  event,
  isSurvey,
  stageUrl,
  exporting,
  onBack,
  onExport,
  onReset,
  onSettings,
  onStats,
  onManifesto,
  onSignOut,
  onTogglePause,
  onToggleRotationPause,
  displayedIdx,
  totalQuestions,
  onSkipQuestion,
  role,
  onRunAnalysis,
  analysisRunning,
}: {
  event: Event;
  isSurvey: boolean;
  stageUrl: string;
  exporting: boolean;
  onBack: () => void;
  onExport: () => void;
  onReset: () => void;
  onSettings: () => void;
  onStats: () => void;
  onManifesto: () => void;
  onSignOut: () => void;
  onTogglePause: () => void;
  onToggleRotationPause: () => void;
  displayedIdx: number;
  totalQuestions: number;
  onSkipQuestion: (direction: "prev" | "next") => void;
  role: EventRole | null;
  onRunAnalysis?: () => void;
  analysisRunning?: boolean;
}) {
  return (
    <header
      className="sticky top-0 z-40 border-b border-rc-fog flex-shrink-0"
      style={{
        background: "rgba(250, 248, 244, 0.92)",
        backdropFilter: "saturate(140%) blur(10px)",
        WebkitBackdropFilter: "saturate(140%) blur(10px)",
      }}
    >
      <div className="px-6 sm:px-8 py-3.5 flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onBack}
          aria-label="Zurück zur Event-Liste"
          className="inline-flex items-center p-1.5 -ml-1.5 text-rc-charcoal hover:text-rc-black hover:bg-rc-mist rounded-full transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <a href="/" aria-label="READYCAL Startseite" className="inline-flex items-center">
          <img src="/brand/readycal-logo.png" alt="READYCAL" className="h-5 w-auto block" />
        </a>
        <span aria-hidden className="text-rc-fog select-none">/</span>
        <span className="text-[14px] font-semibold text-rc-black tracking-[-0.01em] truncate min-w-0 max-w-[24rem]">
          {event.title || "Ohne Titel"}
        </span>
        {can(role, "edit_event_settings") && (
          <button
            type="button"
            onClick={onSettings}
            aria-label="Event-Einstellungen bearbeiten"
            title="Event-Einstellungen"
            className="inline-flex items-center justify-center p-1.5 -mx-0.5 text-rc-slate hover:text-rc-black hover:bg-rc-mist rounded-full transition-colors flex-shrink-0"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={onStats}
          aria-label="Statistiken anzeigen"
          title="Statistiken"
          className="inline-flex items-center justify-center p-1.5 -mx-0.5 text-rc-slate hover:text-rc-black hover:bg-rc-mist rounded-full transition-colors flex-shrink-0"
        >
          <BarChart2 className="w-3.5 h-3.5" />
        </button>
        {can(role, "trigger_ai") && (
          <button
            type="button"
            onClick={onManifesto}
            aria-label="Manifest anzeigen"
            title="Manifest"
            className="inline-flex items-center justify-center p-1.5 -mx-0.5 text-rc-slate hover:text-rc-black hover:bg-rc-mist rounded-full transition-colors flex-shrink-0"
          >
            <FileText className="w-3.5 h-3.5" />
          </button>
        )}
        {onRunAnalysis && (
          <button
            type="button"
            onClick={onRunAnalysis}
            disabled={analysisRunning}
            aria-label="Auswertung für alle Fragen starten"
            title="Auswertung starten: clustert jede Frage, führt die Insight-Tools aus, friert die Ergebnisse ein und schreibt Meta-Analyse und Manifest über alle Fragen"
            className="inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 text-[12px] font-semibold border-[1.5px] border-rc-berry/40 text-rc-berry hover:bg-rc-berry/8 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {analysisRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {analysisRunning ? "Auswertung läuft …" : "Auswertung starten"}
          </button>
        )}
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold tracking-[0.18em] text-rc-black bg-rc-lime/25 border border-rc-lime/40 rounded-pill px-2.5 py-0.5 flex-shrink-0">
          {event.join_code}
        </span>
        {isSurvey && (
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-rc-berry bg-rc-berry/8 border border-rc-berry/25 rounded-pill px-2 py-0.5 flex-shrink-0">
            Survey
          </span>
        )}

        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          {isSurvey && totalQuestions > 1 && (
            <div className="inline-flex items-center gap-0.5 rounded-pill border border-rc-fog bg-transparent pl-1 pr-1 py-0.5">
              <button
                type="button"
                onClick={() => onSkipQuestion("prev")}
                disabled={displayedIdx <= 0}
                aria-label="Vorherige Frage"
                title="Vorherige Frage"
                className="p-1 rounded-full text-rc-charcoal hover:text-rc-black hover:bg-rc-mist disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-[12px] font-semibold tabular-nums text-rc-charcoal px-1 select-none">
                Frage {displayedIdx + 1} / {totalQuestions}
              </span>
              <button
                type="button"
                onClick={() => onSkipQuestion("next")}
                disabled={displayedIdx >= totalQuestions - 1}
                aria-label="Nächste Frage"
                title="Nächste Frage"
                className="p-1 rounded-full text-rc-charcoal hover:text-rc-black hover:bg-rc-mist disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {isSurvey && can(role, "pause_rotation") && (
            <button
              type="button"
              onClick={onToggleRotationPause}
              aria-pressed={event.rotation_interval_seconds == null}
              className={`inline-flex items-center gap-2 rounded-pill px-3 py-1.5 text-[12px] font-semibold border-[1.5px] transition ${
                event.rotation_interval_seconds == null
                  ? "bg-rc-berry/8 border-rc-berry text-rc-berry"
                  : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black"
              }`}
            >
              <Pause className="w-3.5 h-3.5" />
              {event.rotation_interval_seconds == null ? "Rotation pausiert" : "Rotation pausieren"}
            </button>
          )}
          {can(role, "pause_event") && (
            <button
              type="button"
              onClick={onTogglePause}
              aria-label={event.responses_paused ? "Stream fortsetzen" : "Stream pausieren"}
              title={event.responses_paused ? "Stream fortsetzen" : "Stream pausieren"}
              className={`inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-pill px-3 py-1.5 transition-colors border ${
                event.responses_paused
                  ? "bg-rc-lime/20 border-rc-lime/50 text-rc-black hover:bg-rc-lime/35"
                  : "bg-transparent border-rc-fog text-rc-charcoal hover:text-rc-black hover:border-rc-charcoal/30"
              }`}
            >
              {event.responses_paused ? (
                <>
                  <Play className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Stream fortsetzen</span>
                </>
              ) : (
                <>
                  <Pause className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Stream pausieren</span>
                </>
              )}
            </button>
          )}
          <a
            href={stageUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-charcoal hover:text-rc-black border border-rc-fog hover:border-rc-charcoal/30 rounded-pill px-3 py-1.5 transition-colors ease-rc-out"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Stage
          </a>
          {can(role, "export_event") && (
            <button
              type="button"
              onClick={onExport}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-charcoal hover:text-rc-black border border-rc-fog hover:border-rc-charcoal/30 rounded-pill px-3 py-1.5 transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{exporting ? "Exportiere…" : "Export"}</span>
            </button>
          )}
          {can(role, "reset_event") && (
            <button
              type="button"
              onClick={onReset}
              aria-label="Event zurücksetzen"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-berry bg-rc-berry/8 border border-rc-berry/25 hover:bg-rc-berry/15 rounded-pill px-3 py-1.5 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}
          <IconButton aria-label="Abmelden" onClick={onSignOut}>
            <LogOut className="w-3.5 h-3.5" />
          </IconButton>
        </div>
      </div>
    </header>
  );
}

function IconButton({
  children,
  onClick,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center text-rc-charcoal hover:text-rc-black border border-rc-fog hover:border-rc-charcoal/30 rounded-pill w-8 h-8 transition-colors"
      {...rest}
    >
      {children}
    </button>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────

function Sidebar({
  questions,
  selectedQIdx,
  onSelectQuestion,
  onAddQuestion,
  isSurvey,
  participantUrl,
  joinCode,
  visibleResponses,
  status,
  canAddQuestion,
}: {
  questions: Question[];
  selectedQIdx: number;
  onSelectQuestion: (idx: number) => void;
  onAddQuestion: () => void;
  isSurvey: boolean;
  participantUrl: string;
  joinCode: string;
  visibleResponses: number;
  status: Event["status"];
  canAddQuestion: boolean;
}) {
  return (
    <aside className="w-72 flex-shrink-0 border-r border-rc-fog flex flex-col bg-rc-paper">
      <div className="px-4 py-5 border-b border-rc-fog flex-1 overflow-y-auto min-h-0">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">
            Fragen
          </p>
          {canAddQuestion && (
            <button
              type="button"
              onClick={onAddQuestion}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-rc-berry hover:text-rc-berry-700 transition-colors"
            >
              <Plus className="w-3 h-3" /> Hinzufügen
            </button>
          )}
        </div>

        {isSurvey && questions.length > 0 && (
          <div className="flex items-center gap-2 bg-rc-lime/20 border border-rc-lime/40 rounded-xl px-3 py-2 mb-3">
            <span className="relative inline-flex w-1.5 h-1.5 flex-shrink-0">
              <span className="absolute inset-0 rounded-full bg-rc-lime-700 opacity-70 animate-shimmer" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-rc-lime-700" />
            </span>
            <span className="text-[12px] font-semibold text-rc-black">
              Survey läuft. Alle Fragen offen.
            </span>
          </div>
        )}

        {questions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-rc-slate text-[13px] mb-3">Noch keine Fragen.</p>
            {canAddQuestion && (
              <button
                type="button"
                onClick={onAddQuestion}
                className="inline-flex items-center gap-1 text-[13px] font-semibold text-rc-berry hover:text-rc-berry-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Erste Frage anlegen
              </button>
            )}
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {questions.map((q, i) => (
              <li key={q.id}>
                <QuestionListItem
                  question={q}
                  index={i}
                  selected={selectedQIdx === i}
                  isSurvey={isSurvey}
                  onClick={() => onSelectQuestion(i)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-4 py-4 border-b border-rc-fog">
        <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] mb-2">
          Teilnehmer-Link
        </p>
        <div className="bg-rc-mist border border-rc-fog rounded-xl p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <LinkIcon className="w-3.5 h-3.5 text-rc-slate flex-shrink-0" />
            <a
              href={participantUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[12px] text-rc-berry hover:text-rc-berry-700 truncate font-medium min-w-0"
            >
              {participantUrl}
            </a>
          </div>
          <p className="text-[11px] text-rc-slate leading-[1.4]">
            Join-Code{" "}
            <span className="font-mono font-semibold text-rc-black tracking-[0.16em]">{joinCode}</span>
          </p>
        </div>
      </div>

      <div className="px-4 py-4 flex flex-col gap-2.5">
        <SidebarStat
          icon={<Users className="w-3.5 h-3.5" />}
          label="Sichtbare Antworten"
          value={String(visibleResponses)}
        />
        <SidebarStat
          icon={<Activity className="w-3.5 h-3.5" />}
          label="Status"
          value={STATUS_LABEL[status]}
        />
      </div>
    </aside>
  );
}

function QuestionListItem({
  question,
  index,
  selected,
  isSurvey,
  onClick,
}: {
  question: Question;
  index: number;
  selected: boolean;
  isSurvey: boolean;
  onClick: () => void;
}) {
  const badgeCls = isSurvey
    ? "bg-rc-lime/30 text-rc-black border border-rc-lime/40"
    : question.is_open
      ? "bg-rc-berry text-rc-paper"
      : question.closed_at
        ? "bg-rc-stone text-rc-paper"
        : "bg-rc-mist text-rc-charcoal border border-rc-fog";

  const wrapperCls = selected
    ? "bg-rc-berry/8 border-rc-berry/30"
    : "border-transparent hover:bg-rc-mist";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-start gap-3 p-3 rounded-xl text-left border transition-colors ease-rc-out ${wrapperCls}`}
    >
      <span
        className={`flex-shrink-0 mt-0.5 w-6 h-6 rounded-lg inline-flex items-center justify-center text-[12px] font-bold transition-colors ${badgeCls}`}
      >
        {index + 1}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13.5px] text-rc-black leading-[1.4] line-clamp-2 font-medium">
          {question.text || "Ohne Text"}
        </span>
        {!isSurvey && question.is_open && (
          <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-rc-berry font-semibold uppercase tracking-[0.14em]">
            <span className="relative inline-flex w-1 h-1">
              <span className="absolute inset-0 rounded-full bg-rc-berry opacity-70 animate-shimmer" />
              <span className="relative inline-flex w-1 h-1 rounded-full bg-rc-berry" />
            </span>
            Live
          </span>
        )}
        {!isSurvey && !question.is_open && question.closed_at && (
          <span className="inline-block mt-1.5 text-[10px] text-rc-slate font-semibold uppercase tracking-[0.14em]">
            Geschlossen
          </span>
        )}
      </span>
      {selected && <ChevronRight className="w-4 h-4 text-rc-slate flex-shrink-0 mt-1" />}
    </button>
  );
}

function SidebarStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <span className="inline-flex items-center gap-2 text-rc-charcoal font-medium">
        <span className="text-rc-slate">{icon}</span>
        {label}
      </span>
      <span className="text-rc-black font-semibold tabular-nums">{value}</span>
    </div>
  );
}

// ─── Main: question pane and empty state ─────────────────────────────────

function QuestionPane({
  question,
  isSurvey,
  tab,
  onTab,
  onOpen,
  onClose,
  onDelete,
  responses,
  accessToken,
  event,
  tools,
  onEditTools,
  role,
  reload,
}: {
  question: Question;
  isSurvey: boolean;
  tab: Tab;
  onTab: (t: Tab) => void;
  onOpen: (q: Question) => Promise<void>;
  onClose: (q: Question) => Promise<void>;
  onDelete: () => Promise<void>;
  responses: import("../lib/types").Response[];
  accessToken: string;
  event: Event;
  tools: EventTool[];
  onEditTools?: () => void;
  role: EventRole | null;
  reload: () => Promise<void>;
}) {
  const eventId = event.id;
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "live", label: "Antworten" },
    { key: "ai", label: "KI-Tools" },
    ...(can(role, "edit_questions") ? [{ key: "settings" as const, label: "Frage bearbeiten" }] : []),
  ];

  return (
    <>
      <div className="flex items-start justify-between gap-4 px-6 sm:px-8 py-5 border-b border-rc-fog bg-rc-paper flex-shrink-0">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-rc-berry font-semibold tracking-[0.14em] uppercase mb-1.5">
            Frage {question.order_index}
          </p>
          <p className="text-[19px] sm:text-[20px] font-semibold text-rc-black leading-[1.3] tracking-[-0.015em]">
            {question.text || "Ohne Text"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {can(role, "edit_questions") && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="Frage löschen"
              className="p-2.5 text-rc-slate hover:text-rc-danger hover:bg-rc-danger/8 rounded-full transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {!isSurvey && can(role, "open_close_question") && (
            !question.is_open ? (
              <button
                type="button"
                onClick={() => onOpen(question)}
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold bg-rc-berry hover:bg-rc-berry-700 text-rc-paper rounded-pill px-5 py-2.5 transition-colors active:scale-[0.97] shadow-berry"
              >
                <Play className="w-3.5 h-3.5" />
                Öffnen
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onClose(question)}
                className="inline-flex items-center gap-1.5 text-[14px] font-semibold bg-transparent border-[1.5px] border-rc-charcoal text-rc-black hover:bg-rc-black hover:text-rc-paper rounded-pill px-5 py-[9px] transition-colors active:scale-[0.97]"
              >
                <Square className="w-3.5 h-3.5" />
                Schließen
              </button>
            )
          )}
        </div>
      </div>

      <div className="flex gap-1 px-6 sm:px-8 pt-4 flex-shrink-0 bg-rc-paper border-b border-rc-fog">
        {tabs.map(({ key, label }) => {
          const active = tab === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => onTab(key)}
              className={`relative px-3.5 py-2.5 text-[13.5px] font-semibold transition-colors ${
                active ? "text-rc-black" : "text-rc-charcoal hover:text-rc-black"
              }`}
            >
              {label}
              {active && (
                <span className="absolute -bottom-px left-3 right-3 h-0.5 bg-rc-berry rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
        {tab === "live" && (
          <>
            <ResponseModerator responses={responses} question={question} />
            <StageRemoteRow question={question} event={event} role={role} />
            <FacilitatorLiveClusterControls
              question={question}
              responses={responses}
              eventReclusterThreshold={event.recluster_threshold}
              onManualRecluster={async () => {
                await triggerAI({
                  type: "cluster",
                  questionId: question.id,
                  accessToken,
                  triggerReason: "manual",
                });
                // After a manual cluster, auto-clear freeze so the new scheme starts fresh.
                if (question.is_frozen) {
                  await setQuestionFrozen(question.id, false);
                }
              }}
            />
          </>
        )}
        {tab === "ai" && (
          <AIPanel
            question={question}
            responses={responses}
            tools={tools}
            accessToken={accessToken}
            onAIStatus={(status, label) => setAIStatus(eventId, status, label)}
            onEditTools={onEditTools}
          />
        )}
        {tab === "settings" && (
          <QuestionEditor question={question} onDelete={onDelete} reload={reload} />
        )}
      </div>
    </>
  );
}

function NoQuestionState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="bg-rc-paper border border-rc-fog rounded-rc-lg py-16 px-8 max-w-md w-full flex flex-col items-center text-center gap-5 shadow-cardLg">
        <div className="w-14 h-14 rounded-full bg-rc-berry/10 border border-rc-berry/25 flex items-center justify-center">
          <Plus className="w-6 h-6 text-rc-berry" />
        </div>
        <div>
          <h2 className="text-rc-black font-bold text-[20px] tracking-[-0.01em] mb-2">
            Keine Frage ausgewählt.
          </h2>
          <p className="text-rc-charcoal text-[14px] max-w-sm leading-[1.55]">
            Lege deine erste Frage an, um loszulegen.
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 text-rc-paper rounded-pill px-6 py-3 text-[14px] font-semibold active:scale-[0.97] transition shadow-berry"
        >
          <Plus className="w-4 h-4" />
          Erste Frage anlegen
        </button>
      </div>
    </div>
  );
}

// ─── Event statistics panel ───────────────────────────────────────────────

function EventStatsPanel({ eventId, aiBudgetUsd }: { eventId: string; aiBudgetUsd: number }) {
  const [stats, setStats] = useState<EventStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getEventStats(eventId);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-rc-slate text-[14px] py-8">
        <Loader2 className="w-4 h-4 animate-spin" />
        Lade Statistiken…
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-rc-danger text-[14px] py-8">
        Fehler: {error ?? "Keine Daten"}
      </div>
    );
  }

  const { total_responses, responses_by_status, current_concurrency,
    ai_call_count_by_function, ai_calls_by_model, ai_spend_usd_total } = stats;

  const aiTotal = Object.values(ai_call_count_by_function).reduce((a, b) => a + b, 0);

  const KNOWN_FUNCTIONS = [
    "workshop-ai:cluster",
    "workshop-ai:insight",
    "moderate-response",
    "moderate-response:wordlist",
  ];
  // Include any unexpected function names from the log
  const allFunctions = Array.from(
    new Set([...KNOWN_FUNCTIONS, ...Object.keys(ai_call_count_by_function)]),
  ).filter((fn) => ai_call_count_by_function[fn] !== undefined);

  const KNOWN_MODELS = ["gpt-4o", "gpt-4o-mini", "wordlist-v1"];
  const allModels = Array.from(
    new Set([...KNOWN_MODELS, ...Object.keys(ai_calls_by_model)]),
  ).filter((m) => ai_calls_by_model[m] !== undefined);

  const statCard = (label: string, value: string | number, sub?: React.ReactNode) => (
    <div className="bg-rc-paper border border-rc-fog rounded-xl px-5 py-4 flex flex-col gap-3 shadow-card">
      <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">{label}</p>
      <p className="text-[28px] font-bold text-rc-black tabular-nums tracking-[-0.02em] leading-none">
        {typeof value === "number" ? value.toLocaleString("de-DE") : value}
      </p>
      {sub && <div className="flex flex-col gap-1 border-t border-rc-fog/70 pt-3">{sub}</div>}
    </div>
  );

  const subRow = (label: string, value: string | number) => (
    <div key={label} className="flex items-center justify-between gap-4 text-[13px]">
      <span className="text-rc-charcoal truncate">{label}</span>
      <span className="text-rc-black font-semibold tabular-nums flex-shrink-0">
        {typeof value === "number" ? value.toLocaleString("de-DE") : value}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-5 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-rc-slate">Live-Daten — wird nicht automatisch aktualisiert.</p>
        <button
          type="button"
          onClick={() => { void load(); }}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-charcoal hover:text-rc-black border border-rc-fog hover:border-rc-charcoal/30 rounded-pill px-3 py-1.5 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {statCard(
          "Antworten gesamt",
          total_responses,
          <>
            {subRow("clean", responses_by_status.clean)}
            {subRow("borderline", responses_by_status.borderline)}
            {subRow("block", responses_by_status.block)}
            {subRow("pending", responses_by_status.pending)}
          </>,
        )}

        {statCard(
          "Aktive Teilnehmer (5 min)",
          current_concurrency,
        )}

        {statCard(
          "AI-Aufrufe gesamt",
          aiTotal,
          allFunctions.length > 0 ? (
            <>
              {allFunctions.map((fn) =>
                subRow(fn, ai_call_count_by_function[fn] ?? 0),
              )}
            </>
          ) : subRow("(keine Daten)", "—"),
        )}

        {statCard(
          "AI-Kosten gesamt",
          `$${ai_spend_usd_total.toFixed(4)}`,
          allModels.length > 0 ? (
            <>
              {allModels.map((m) =>
                subRow(m, `${(ai_calls_by_model[m] ?? 0).toLocaleString("de-DE")} Aufrufe`),
              )}
            </>
          ) : undefined,
        )}

        {statCard(
          "KI-Budget",
          `$${ai_spend_usd_total.toFixed(2)} / $${aiBudgetUsd.toFixed(2)}`,
          <>
            {aiBudgetUsd > 0 && ai_spend_usd_total / aiBudgetUsd >= 1 ? (
              <p className="text-[13px] font-semibold text-rc-danger">
                KI-Budget erreicht. Cluster und Insights pausieren, bis das Budget in den Einstellungen erhöht wird.
              </p>
            ) : aiBudgetUsd > 0 && ai_spend_usd_total / aiBudgetUsd >= 0.8 ? (
              <p className="text-[13px] font-semibold text-rc-berry">
                KI-Budget fast erreicht ({Math.round((ai_spend_usd_total / aiBudgetUsd) * 100)} %).
              </p>
            ) : (
              subRow("verbraucht", aiBudgetUsd > 0 ? `${Math.round((ai_spend_usd_total / aiBudgetUsd) * 100)} %` : "—")
            )}
            {subRow("Preisliste", "Mai 2026")}
          </>,
        )}
      </div>
    </div>
  );
}

// ─── Event settings (form) ────────────────────────────────────────────────

type SettingsTab = "grundlagen" | "team" | "ki" | "sicherheit" | "stroeme";

type SaveState = "saving" | "saved" | "error";

function SaveHint({ state }: { state?: SaveState }) {
  if (!state) return null;
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rc-slate">
        <Loader2 className="w-3 h-3 animate-spin" /> Speichert…
      </span>
    );
  }
  if (state === "saved") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rc-berry">
        <Check className="w-3 h-3" strokeWidth={3} /> Gespeichert
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rc-danger">
      <AlertTriangle className="w-3 h-3" /> Nicht gespeichert
    </span>
  );
}

// Section header with an inline autosave status, so each block reports its own
// save state (there is no page-level Save button anymore — every control
// persists on change/blur).
function SettingsSection({ title, state, children }: { title: ReactNode; state?: SaveState; children: ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-2 min-h-[18px]">
        <span className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] flex items-center gap-1.5">
          {title}
        </span>
        <SaveHint state={state} />
      </div>
      {children}
    </div>
  );
}

function EventSettingsEditor({
  event,
  role,
  questions,
  onSave,
  initialTab = "grundlagen",
}: {
  event: Event;
  role: EventRole | null;
  questions: Question[];
  onSave: (e: Event) => void;
  initialTab?: SettingsTab;
}) {
  const tabs: Array<{ key: SettingsTab; label: string }> = [
    { key: "grundlagen", label: "Grundlagen" },
    ...(role === "owner" || role === "co_admin" ? [{ key: "team" as const, label: "Team & Zugang" }] : []),
    { key: "ki", label: "KI & Analyse" },
    { key: "sicherheit", label: "Sicherheit & Moderation" },
    { key: "stroeme", label: "Datenströme" },
  ];
  const safeInitial = tabs.some((t) => t.key === initialTab) ? initialTab : "grundlagen";
  const [activeTab, setActiveTab] = useState<SettingsTab>(safeInitial);

  const [title, setTitle] = useState(event.title ?? "");
  const [description, setDescription] = useState(event.description ?? "");
  const [mode, setMode] = useState<"facilitated" | "survey">(event.event_mode);
  const [autoRotate, setAutoRotate] = useState(event.rotation_interval_seconds !== null);
  const [interval, setIntervalValue] = useState(String(event.rotation_interval_seconds ?? 60));
  const [opensAt, setOpensAt] = useState(isoToLocalInput(event.survey_opens_at ?? null));
  const [closesAt, setClosesAt] = useState(isoToLocalInput(event.survey_closes_at ?? null));
  const [surveyIntroEnabled, setSurveyIntroEnabled] = useState(event.survey_intro_enabled);
  const [linkCopied, setLinkCopied] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(event.system_prompt ?? "");
  const [moderationEnabled, setModerationEnabled] = useState(event.moderation_enabled);
  const initialRules: ModerationRules = event.moderation_rules ?? {
    obscenity: true,
    hate: true,
    personal_attack: true,
    custom: "",
  };
  const [moderationRules, setModerationRules] = useState<Omit<ModerationRules, "custom">>({
    obscenity: initialRules.obscenity,
    hate: initialRules.hate,
    personal_attack: initialRules.personal_attack,
  });
  const [customRules, setCustomRules] = useState(initialRules.custom ?? "");
  const [borderlineMode, setBorderlineMode] = useState<"auto_publish_30s" | "hold_for_review">(
    event.borderline_mode ?? "auto_publish_30s",
  );
  const [capPerQuestion, setCapPerQuestion] = useState(String(event.cap_per_question ?? 5));
  const [capPerEvent, setCapPerEvent] = useState(String(event.cap_per_event ?? 30));
  const [aiBudget, setAiBudget] = useState(String(event.ai_budget_usd ?? 15));
  const [clusterPhase, setClusterPhase] = useState(String(event.cluster_phase_seconds ?? 20));
  const [insightPhase, setInsightPhase] = useState(String(event.insight_phase_seconds ?? 14));
  const [maxClusters, setMaxClusters] = useState<4 | 6 | 8>(
    (event.max_clusters === 4 || event.max_clusters === 6 || event.max_clusters === 8
      ? event.max_clusters
      : 6) as 4 | 6 | 8,
  );
  const [insightThreshold, setInsightThreshold] = useState(
    String(event.insight_trigger_threshold ?? 10),
  );

  const [status, setStatus] = useState<Record<string, SaveState>>({});

  // Every control persists independently (autosave). Toggles/segmented buttons
  // save on click; text/number inputs save on blur after normalisation.
  const persist = useCallback(
    async (key: string, patch: Partial<Event>) => {
      setStatus((s) => ({ ...s, [key]: "saving" }));
      try {
        await updateEvent(event.id, patch);
        onSave({ ...event, ...patch });
        setStatus((s) => ({ ...s, [key]: "saved" }));
        window.setTimeout(() => {
          setStatus((s) => {
            if (s[key] !== "saved") return s;
            const next = { ...s };
            delete next[key];
            return next;
          });
        }, 2200);
      } catch {
        setStatus((s) => ({ ...s, [key]: "error" }));
      }
    },
    [event, onSave],
  );

  const saveRotation = (nextMode: "facilitated" | "survey", nextAuto: boolean, nextInterval: string) => {
    const val = nextMode === "survey" && nextAuto ? Math.max(10, parseInt(nextInterval, 10) || 60) : null;
    void persist("modus", { event_mode: nextMode, rotation_interval_seconds: val });
  };
  const saveModerationRules = (rules: Omit<ModerationRules, "custom">, custom: string) => {
    void persist("moderation", { moderation_rules: { ...rules, custom: custom.trim() } as ModerationRules });
  };

  const numCls =
    "w-24 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black text-center font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15";
  const textCls =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 resize-none focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors leading-[1.55]";
  const segCls = (selected: boolean) =>
    `text-left px-4 py-3 rounded-xl text-[14px] font-semibold border-[1.5px] transition-colors ease-rc-out ${
      selected
        ? "bg-rc-berry/8 border-rc-berry text-rc-berry"
        : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black hover:border-rc-charcoal/40"
    }`;

  return (
    <div className="max-w-2xl">
      <div className="flex gap-1 border-b border-rc-fog mb-7 overflow-x-auto">
        {tabs.map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`px-3.5 py-2.5 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active
                  ? "border-rc-berry text-rc-berry"
                  : "border-transparent text-rc-charcoal hover:text-rc-black"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Grundlagen ─────────────────────────────────────────────────── */}
      {activeTab === "grundlagen" && (
        <div className="flex flex-col gap-6">
          <SettingsSection title="Titel" state={status.title}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title.trim() !== (event.title ?? "") && void persist("title", { title: title.trim() })}
              maxLength={120}
              placeholder="Name des Events"
              className="w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] font-semibold text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors"
            />
          </SettingsSection>

          <SettingsSection title="Beschreibung" state={status.description}>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => description.trim() !== (event.description ?? "") && void persist("description", { description: description.trim() })}
              rows={3}
              placeholder="Worum geht es? Diese Beschreibung gibt der KI zusätzlichen Kontext."
              className={textCls}
            />
          </SettingsSection>

          <SettingsSection title="Event-Modus" state={status.modus}>
            <div className="grid grid-cols-2 gap-2">
              {(["facilitated", "survey"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); saveRotation(m, autoRotate, interval); }}
                  aria-pressed={mode === m}
                  className={segCls(mode === m)}
                >
                  {m === "facilitated" ? "Workshop" : "Survey"}
                </button>
              ))}
            </div>
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              {mode === "facilitated"
                ? "Du steuerst, welche Frage offen ist und wann die KI-Analyse läuft."
                : "Alle Fragen sind gleichzeitig offen. KI läuft automatisch, sobald Antworten reinkommen."}
            </p>
          </SettingsSection>

          {mode === "survey" && (
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoRotate}
                  onClick={() => { const v = !autoRotate; setAutoRotate(v); saveRotation(mode, v, interval); }}
                  className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${autoRotate ? "bg-rc-berry" : "bg-rc-fog"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-rc-paper shadow transition-transform ${autoRotate ? "translate-x-4" : "translate-x-0"}`} />
                </button>
                <span className="text-[14px] text-rc-black font-medium">Fragen automatisch wechseln</span>
              </label>
              {autoRotate && (
                <div className="flex items-center gap-3 mt-3 pl-[3.25rem]">
                  <input
                    type="number"
                    value={interval}
                    onChange={(e) => setIntervalValue(e.target.value)}
                    onBlur={() => saveRotation(mode, autoRotate, interval)}
                    min="10"
                    max="3600"
                    className={numCls}
                  />
                  <span className="text-rc-charcoal text-[13px]">Sekunden pro Frage</span>
                </div>
              )}
            </div>
          )}

          <SettingsSection title="Vorab-Umfrage (asynchron)" state={status.window}>
            <p className="text-[12px] text-rc-slate mb-3 leading-[1.5]">
              Im Zeitfenster beantworten Teilnehmende alle Fragen in ihrem eigenen
              Tempo über den Join-Link — ganz ohne laufende Live-Session. Nach dem
              Ende gilt wieder der normale Live-Modus.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">Start</span>
                <input
                  type="datetime-local"
                  value={opensAt}
                  onChange={(e) => setOpensAt(e.target.value)}
                  onBlur={() =>
                    void persist("window", {
                      survey_opens_at: localInputToIso(opensAt),
                      survey_closes_at: localInputToIso(closesAt),
                    })
                  }
                  className="bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">Ende</span>
                <input
                  type="datetime-local"
                  value={closesAt}
                  onChange={(e) => setClosesAt(e.target.value)}
                  onBlur={() =>
                    void persist("window", {
                      survey_opens_at: localInputToIso(opensAt),
                      survey_closes_at: localInputToIso(closesAt),
                    })
                  }
                  className="bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
                />
              </label>
            </div>
            <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-rc-fog">
              <div className="flex-1 min-w-0">
                <span className="text-[13px] font-semibold text-rc-black">Beschreibung als Startbildschirm</span>
                <p className="text-[12px] text-rc-slate mt-0.5 leading-[1.5]">
                  Zeigt die Event-Beschreibung als Willkommensscreen, bevor die erste Frage erscheint. Wirkt nur, wenn eine Beschreibung hinterlegt ist.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <SaveHint state={status.surveyIntro} />
                <button
                  type="button"
                  role="switch"
                  aria-checked={surveyIntroEnabled}
                  onClick={() => { const v = !surveyIntroEnabled; setSurveyIntroEnabled(v); void persist("surveyIntro", { survey_intro_enabled: v }); }}
                  className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${surveyIntroEnabled ? "bg-rc-berry" : "bg-rc-fog"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-rc-paper shadow transition-transform ${surveyIntroEnabled ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <code className="text-[12px] font-mono text-rc-charcoal bg-rc-mist border border-rc-fog rounded-lg px-2.5 py-1.5 select-all">
                {`${window.location.origin}/join/${event.join_code}`}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(`${window.location.origin}/join/${event.join_code}`);
                  setLinkCopied(true);
                  window.setTimeout(() => setLinkCopied(false), 2000);
                }}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-rc-berry hover:text-rc-berry-700 transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                {linkCopied ? "Kopiert" : "Link kopieren"}
              </button>
            </div>
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              Tipp: Das Antwort-Limit pro Gerät („Sicherheit &amp; Moderation“) sollte
              mindestens der Fragenanzahl entsprechen (aktuell {event.cap_per_event} pro Gerät).
            </p>
          </SettingsSection>

          <SettingsSection title="Vergleich nach Gruppe" state={status.segment}>
            <p className="text-[12px] text-rc-slate mb-3 leading-[1.5]">
              Die Bühne kann jede Frage nach den Antworten auf eine Einordnungsfrage
              aufteilen (zum Beispiel nach Rolle). Geeignet sind Einfachauswahl-Fragen
              ohne „Sonstiges“. Gruppen mit weniger als drei Antworten werden nicht
              einzeln gezeigt.
            </p>
            <select
              value={event.segment_question_id ?? ""}
              aria-label="Vergleichsfrage auswählen"
              onChange={(e) => void persist("segment", { segment_question_id: e.target.value || null })}
              className="w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors"
            >
              <option value="">Keine Vergleichsfrage</option>
              {questions.filter(isEligibleSegmentQuestion).map((q) => (
                <option key={q.id} value={q.id}>Frage {q.order_index}: {q.text.slice(0, 80)}</option>
              ))}
            </select>
          </SettingsSection>
        </div>
      )}

      {/* ── Team & Zugang ──────────────────────────────────────────────── */}
      {activeTab === "team" && (role === "owner" || role === "co_admin") && (
        <div className="flex flex-col gap-6">
          {role === "owner" && <CoFacilitatorCard eventId={event.id} />}
          <PresentationKeyCard eventId={event.id} joinCode={event.join_code} />
        </div>
      )}

      {/* ── KI & Analyse ───────────────────────────────────────────────── */}
      {activeTab === "ki" && (
        <div className="flex flex-col gap-6">
          <SettingsSection title="KI-Kontext (optional)" state={status.systemPrompt}>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              onBlur={() => systemPrompt.trim() !== (event.system_prompt ?? "") && void persist("systemPrompt", { system_prompt: systemPrompt.trim().length > 0 ? systemPrompt.trim() : null })}
              rows={6}
              placeholder={
                "Optional. Beschreibe Zielgruppe, Setting, Themenfokus und gewünschten Tonfall. " +
                "Hilft der KI, schärfere Cluster und Beobachtungen zu produzieren. " +
                "Beispiel: „Workshop mit 40 Stadtplaner:innen in Hannover. Thema: ungenutzte städtische Räume. Sprich Fachpublikum an, ohne in Beratersprache zu verfallen.“"
              }
              className={textCls}
            />
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              Leer lassen, um nur Titel und Beschreibung als Kontext zu verwenden. Standardvoice und Standardsprache (Deutsch) sind immer aktiv.
            </p>
          </SettingsSection>

          <SettingsSection title="Max. Themen" state={status.maxClusters}>
            <div className="grid grid-cols-3 gap-2 max-w-xs">
              {([4, 6, 8] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => { setMaxClusters(n); void persist("maxClusters", { max_clusters: n }); }}
                  aria-pressed={maxClusters === n}
                  className={segCls(maxClusters === n).replace("text-left ", "")}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              Wie viele Themen die KI aus den Antworten höchstens bildet. Inkl. Sammel-Cluster „Sonstiges“.
            </p>
          </SettingsSection>

          <SettingsSection title="KI-Beobachtungen" state={status.insightThreshold}>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                value={insightThreshold}
                onChange={(e) => setInsightThreshold(e.target.value)}
                onBlur={() => void persist("insightThreshold", { insight_trigger_threshold: Math.max(1, parseInt(insightThreshold, 10) || 10) })}
                className={numCls}
              />
              <span className="text-rc-charcoal text-[13px]">neue Antworten zwischen zwei Läufen</span>
            </div>
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              Wie viele neue Antworten mindestens eingehen müssen, bevor die KI erneut eine Beobachtung erzeugt. Höher = ruhigere Bühne.
            </p>
          </SettingsSection>

          <SettingsSection title="Bühne · Karussell" state={status.stage}>
            <p className="text-[12px] text-rc-slate mb-3 leading-[1.5]">
              Auf der Bühne wechseln Cluster und KI-Insights automatisch durch. So sieht der Raum auch ohne Scrollen alles.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={clusterPhase}
                  onChange={(e) => setClusterPhase(e.target.value)}
                  onBlur={() => void persist("stage", { cluster_phase_seconds: Math.max(5, parseInt(clusterPhase, 10) || 20), insight_phase_seconds: Math.max(5, parseInt(insightPhase, 10) || 14) })}
                  min="5"
                  max="600"
                  className="w-20 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black text-center font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
                />
                <span className="text-rc-charcoal text-[13px]">Sek. pro Cluster-Phase</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={insightPhase}
                  onChange={(e) => setInsightPhase(e.target.value)}
                  onBlur={() => void persist("stage", { cluster_phase_seconds: Math.max(5, parseInt(clusterPhase, 10) || 20), insight_phase_seconds: Math.max(5, parseInt(insightPhase, 10) || 14) })}
                  min="5"
                  max="600"
                  className="w-20 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black text-center font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
                />
                <span className="text-rc-charcoal text-[13px]">Sek. pro Insight</span>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection title="Kostenlimit KI (USD, pro Event)" state={status.budget}>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={aiBudget}
                onChange={(e) => setAiBudget(e.target.value)}
                onBlur={() => void persist("budget", { ai_budget_usd: Math.max(0, parseFloat(aiBudget.replace(",", ".")) || 15) })}
                min="0"
                step="1"
                className={numCls}
              />
              <span className="text-rc-charcoal text-[13px]">US-Dollar für Cluster, Insights, Manifest und Moderation</span>
            </div>
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              Ab 100 % pausieren Cluster und Insights, bis das Limit erhöht wird. Die Moderation läuft weiter. Verbrauch: Tab Statistiken.
            </p>
          </SettingsSection>

          <div className="pt-4 border-t border-rc-fog">
            <ToolsEditor eventId={event.id} />
          </div>
        </div>
      )}

      {/* ── Sicherheit & Moderation ────────────────────────────────────── */}
      {activeTab === "sicherheit" && (
        <div className="flex flex-col gap-6">
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5" />
                Content Moderation
              </span>
              <div className="flex items-center gap-3">
                <SaveHint state={status.moderation} />
                <button
                  type="button"
                  role="switch"
                  aria-checked={moderationEnabled}
                  onClick={() => { const v = !moderationEnabled; setModerationEnabled(v); void persist("moderation", { moderation_enabled: v }); }}
                  className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${moderationEnabled ? "bg-rc-berry" : "bg-rc-fog"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-rc-paper shadow transition-transform ${moderationEnabled ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
            <p className="text-[12px] text-rc-slate mb-3 leading-[1.5]">
              KI prüft jede Antwort, bevor sie auf der Bühne erscheint. Standard: Workshop aus, Survey an.
            </p>
            <div className={`transition-opacity ${moderationEnabled ? "" : "opacity-60 pointer-events-none"}`}>
              <div className="flex flex-col gap-2 mb-4">
                {([
                  ["obscenity", "Obszönität / vulgäre Sprache"],
                  ["hate", "Hassrede & Rassismus"],
                  ["personal_attack", "Persönliche Angriffe"],
                ] as const).map(([key, label]) => {
                  const checked = moderationRules[key];
                  return (
                    <label key={key} className="flex items-center gap-3 cursor-pointer group">
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={checked}
                        onClick={() => {
                          const next = { ...moderationRules, [key]: !moderationRules[key] };
                          setModerationRules(next);
                          saveModerationRules(next, customRules);
                        }}
                        className={`w-5 h-5 rounded-md border-[1.5px] flex items-center justify-center flex-shrink-0 transition-colors ${checked ? "bg-rc-berry border-rc-berry" : "bg-rc-paper border-rc-fog group-hover:border-rc-charcoal/40"}`}
                      >
                        {checked && <Check className="w-3.5 h-3.5 text-rc-paper" strokeWidth={3} />}
                      </button>
                      <span className="text-[14px] text-rc-black">{label}</span>
                    </label>
                  );
                })}
              </div>
              <label className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2">
                Zusätzliche Regeln (optional)
              </label>
              <textarea
                value={customRules}
                onChange={(e) => setCustomRules(e.target.value)}
                onBlur={() => customRules.trim() !== (initialRules.custom ?? "").trim() && saveModerationRules(moderationRules, customRules)}
                rows={4}
                placeholder={"Eine Regel pro Zeile. z. B. „Keine Wettbewerbernamen“, „Bleibt beim Thema X“."}
                className={textCls}
              />

              <label className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mt-4 mb-2">
                Grenzwertige Beiträge
              </label>
              <div className="grid grid-cols-1 gap-2">
                {([
                  ["auto_publish_30s", "Nach 30 Sekunden zeigen", "Grenzwertige Beiträge erscheinen nach kurzer Wartezeit automatisch. Gut für den Fluss im Workshop."],
                  ["hold_for_review", "Erst nach Freigabe zeigen", "Grenzwertige Beiträge bleiben verborgen, bis du sie im Tab Live freigibst. Empfohlen für öffentliche Bürgerdialoge."],
                ] as const).map(([value, label, desc]) => {
                  const selected = borderlineMode === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => { setBorderlineMode(value); void persist("borderline", { borderline_mode: value }); }}
                      aria-pressed={selected}
                      className={`text-left px-4 py-3 rounded-xl border-[1.5px] transition-colors ease-rc-out ${selected ? "bg-rc-berry/8 border-rc-berry" : "bg-rc-paper border-rc-fog hover:border-rc-charcoal/40"}`}
                    >
                      <span className={`block text-[14px] font-semibold ${selected ? "text-rc-berry" : "text-rc-black"}`}>{label}</span>
                      <span className="block text-[12px] text-rc-slate mt-0.5 leading-[1.45]">{desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <SettingsSection title="Beitragslimits (pro Gerät, 24 h)" state={status.caps}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={capPerQuestion}
                  onChange={(e) => setCapPerQuestion(e.target.value)}
                  onBlur={() => void persist("caps", { cap_per_question: Math.max(1, parseInt(capPerQuestion, 10) || 5), cap_per_event: Math.max(1, parseInt(capPerEvent, 10) || 30) })}
                  min="1"
                  max="999"
                  className={numCls}
                />
                <span className="text-rc-charcoal text-[13px]">Beiträge pro Frage</span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  value={capPerEvent}
                  onChange={(e) => setCapPerEvent(e.target.value)}
                  onBlur={() => void persist("caps", { cap_per_question: Math.max(1, parseInt(capPerQuestion, 10) || 5), cap_per_event: Math.max(1, parseInt(capPerEvent, 10) || 30) })}
                  min="1"
                  max="9999"
                  className={numCls}
                />
                <span className="text-rc-charcoal text-[13px]">Beiträge gesamt im Event</span>
              </div>
            </div>
            <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
              Überschreitet ein Gerät das Limit, erhält es eine freundliche Hinweismeldung.
            </p>
          </SettingsSection>
        </div>
      )}

      {/* ── Datenströme ────────────────────────────────────────────────── */}
      {activeTab === "stroeme" && (
        <div className="flex flex-col gap-6">
          <StreamsPanel event={event} />
        </div>
      )}
    </div>
  );
}

// ─── Streams panel ────────────────────────────────────────────────────────

function StreamsPanel({ event }: { event: Event }) {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newGeoTag, setNewGeoTag] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setStreams(await listStreams(event.id));
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => { void reload(); }, [reload]);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      await createStream(event.id, { label: newLabel.trim(), geo_tag: newGeoTag.trim() || null });
      setNewLabel("");
      setNewGeoTag("");
      await reload();
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteStream(id);
    await reload();
  };

  const handleLabelChange = async (stream: Stream, label: string) => {
    await updateStream(stream.id, { label });
    setStreams((prev) => prev.map((s) => s.id === stream.id ? { ...s, label } : s));
  };

  const handleGeoTagChange = async (stream: Stream, geo_tag: string) => {
    await updateStream(stream.id, { geo_tag: geo_tag || null });
    setStreams((prev) => prev.map((s) => s.id === stream.id ? { ...s, geo_tag: geo_tag || null } : s));
  };

  const moveStream = async (idx: number, dir: -1 | 1) => {
    const swapped = [...streams];
    const target = idx + dir;
    if (target < 0 || target >= swapped.length) return;
    [swapped[idx], swapped[target]] = [swapped[target], swapped[idx]];
    setStreams(swapped);
    await reorderStreams(swapped.map((s) => s.id));
  };

  const copyUrl = (stream: Stream) => {
    const url = `${window.location.origin}/join/${event.join_code}/${stream.code}`;
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedId(stream.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <MapPin className="w-3.5 h-3.5 text-rc-charcoal" />
        <span className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">
          Streams
        </span>
      </div>
      <p className="text-[12px] text-rc-slate leading-[1.5] -mt-2">
        Ordne Antworten geografischen oder logischen Gruppen zu. Teile den Stream-Link mit einer Gruppe,
        damit ihre Eingaben tagged werden.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-rc-slate text-[13px]">
          <Loader2 className="w-4 h-4 animate-spin" />
          Lädt…
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {streams.length === 0 && (
            <p className="text-[13px] text-rc-slate italic">Noch keine Streams angelegt.</p>
          )}
          {streams.map((stream, idx) => (
            <StreamRow
              key={stream.id}
              stream={stream}
              event={event}
              isFirst={idx === 0}
              isLast={idx === streams.length - 1}
              copied={copiedId === stream.id}
              onCopy={() => copyUrl(stream)}
              onMoveUp={() => moveStream(idx, -1)}
              onMoveDown={() => moveStream(idx, 1)}
              onDelete={() => handleDelete(stream.id)}
              onLabelChange={(label) => handleLabelChange(stream, label)}
              onGeoTagChange={(geo_tag) => handleGeoTagChange(stream, geo_tag)}
            />
          ))}
        </div>
      )}

      <div className="flex flex-col gap-2 pt-2 border-t border-rc-fog/60">
        <div className="flex gap-2">
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Stream-Name, z. B. Linden-Nord"
            maxLength={80}
            className="flex-1 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors"
          />
          <select
            value={newGeoTag}
            onChange={(e) => setNewGeoTag(e.target.value)}
            aria-label="Stadtbezirk auswählen"
            className="w-44 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 text-[14px] text-rc-black focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors"
          >
            <option value="">Stadtbezirk (optional)</option>
            {STADTTEIL_SLUGS.map((slug) => (
              <option key={slug} value={slug}>
                {STADTTEIL_DISPLAY_NAMES[slug]}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={adding || !newLabel.trim()}
          className="inline-flex items-center gap-2 bg-rc-black hover:bg-rc-graphite disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper font-semibold text-[13px] rounded-pill px-4 py-2 transition-colors w-fit active:scale-[0.97]"
        >
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Stream hinzufügen
        </button>
      </div>
    </div>
  );
}

function StreamRow({
  stream,
  event,
  isFirst,
  isLast,
  copied,
  onCopy,
  onMoveUp,
  onMoveDown,
  onDelete,
  onLabelChange,
  onGeoTagChange,
}: {
  stream: Stream;
  event: Event;
  isFirst: boolean;
  isLast: boolean;
  copied: boolean;
  onCopy: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onLabelChange: (label: string) => void;
  onGeoTagChange: (geo_tag: string) => void;
}) {
  const [label, setLabel] = useState(stream.label);
  const [geoTag, setGeoTag] = useState(stream.geo_tag ?? "");
  const [editingLabel, setEditingLabel] = useState(false);
  const [editingGeo, setEditingGeo] = useState(false);

  return (
    <div className="bg-rc-cream border border-rc-fog rounded-xl px-4 py-3 flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            aria-label="Nach oben"
            className="p-0.5 text-rc-slate hover:text-rc-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            aria-label="Nach unten"
            className="p-0.5 text-rc-slate hover:text-rc-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {editingLabel ? (
            <input
              type="text"
              value={label}
              autoFocus
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => { setEditingLabel(false); onLabelChange(label); }}
              onKeyDown={(e) => { if (e.key === "Enter") { setEditingLabel(false); onLabelChange(label); } }}
              maxLength={80}
              className="w-full bg-rc-paper border-[1.5px] border-rc-berry rounded-lg px-2 py-1 text-[14px] font-semibold text-rc-black focus:outline-none focus:ring-2 focus:ring-rc-berry/15"
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingLabel(true)}
              className="text-left text-[14px] font-semibold text-rc-black hover:text-rc-berry transition-colors truncate"
            >
              {label}
            </button>
          )}

          {editingGeo ? (
            <select
              value={geoTag}
              autoFocus
              onChange={(e) => {
                const v = e.target.value;
                setGeoTag(v);
                setEditingGeo(false);
                onGeoTagChange(v);
              }}
              onBlur={() => setEditingGeo(false)}
              aria-label="Stadtbezirk auswählen"
              className="w-full bg-rc-paper border-[1.5px] border-rc-berry rounded-lg px-2 py-1 text-[12px] text-rc-black focus:outline-none focus:ring-2 focus:ring-rc-berry/15"
            >
              <option value="">Kein Stadtbezirk</option>
              {STADTTEIL_SLUGS.map((slug) => (
                <option key={slug} value={slug}>
                  {STADTTEIL_DISPLAY_NAMES[slug]}
                </option>
              ))}
            </select>
          ) : (
            <button
              type="button"
              onClick={() => setEditingGeo(true)}
              className="text-left text-[12px] text-rc-slate hover:text-rc-charcoal transition-colors"
            >
              {geoTag
                ? (STADTTEIL_DISPLAY_NAMES[geoTag as keyof typeof STADTTEIL_DISPLAY_NAMES] ?? geoTag)
                : <span className="italic">Kein Stadtbezirk — klicken zum Auswählen</span>}
            </button>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center font-mono text-[11px] font-semibold bg-rc-paper border border-rc-fog rounded-md px-2 py-0.5 text-rc-charcoal tracking-[0.1em]">
              {stream.code}
            </span>
            <button
              type="button"
              onClick={onCopy}
              className="inline-flex items-center gap-1.5 text-[12px] text-rc-charcoal hover:text-rc-black transition-colors"
              title={`/join/${event.join_code}/${stream.code}`}
            >
              <Copy className="w-3 h-3" />
              {copied ? "Kopiert!" : "Link kopieren"}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onDelete}
          aria-label="Stream löschen"
          className="flex-shrink-0 p-1.5 text-rc-slate hover:text-rc-danger hover:bg-rc-danger/8 rounded-lg transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Question editor ──────────────────────────────────────────────────────

function defaultConfigForType(type: QuestionType): QuestionConfig {
  if (type === "nps") return DEFAULT_NPS_CONFIG;
  if (type === "multiple_choice") return DEFAULT_MCQ_CONFIG;
  if (type === "polar") return DEFAULT_POLAR_CONFIG;
  if (type === "likert") return DEFAULT_LIKERT_CONFIG;
  return {};
}

function validateConfig(type: QuestionType, config: QuestionConfig): string | null {
  if (type === "nps") {
    const c = config as NPSConfig;
    if (!c.labels.min.trim() || !c.labels.max.trim()) return "Bitte beide NPS-Labels ausfüllen.";
  }
  if (type === "multiple_choice") {
    const c = config as MCQConfig;
    if (c.options.length < 2) return "Mindestens 2 Optionen erforderlich.";
    if (c.options.some((o) => !o.label.trim())) return "Bitte alle Optionen-Labels ausfüllen.";
    const keys = c.options.map((o) => o.key);
    if (new Set(keys).size !== keys.length) return "Optionen-Schlüssel müssen eindeutig sein.";
  }
  if (type === "polar") {
    const c = config as PolarConfig;
    if (!c.side_a.label.trim() || !c.side_b.label.trim()) return "Bitte beide Seiten-Labels ausfüllen.";
    if (c.side_a.key === c.side_b.key) return "Die Seiten-Schlüssel müssen sich unterscheiden.";
  }
  if (type === "likert") {
    const c = config as LikertConfig;
    if (!c.labels.min.trim() || !c.labels.max.trim()) return "Bitte beide Skalen-Labels ausfüllen.";
  }
  return null;
}

function QuestionEditor({
  question,
  onDelete,
  reload,
}: {
  question: Question;
  onDelete: () => Promise<void>;
  reload: () => Promise<void>;
}) {
  const [text, setText] = useState(question.text);
  const [hint, setHint] = useState(question.hint ?? "");
  const [priming, setPriming] = useState(question.priming_text ?? "");
  const [seedClusters, setSeedClusters] = useState<SeedCluster[] | null>(question.seed_clusters ?? null);
  const [formType, setFormType] = useState<QuestionType>(question.question_type ?? "open_text");
  const [formConfig, setFormConfig] = useState<QuestionConfig>(question.config ?? {});
  const [hasResponses, setHasResponses] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    setText(question.text);
    setHint(question.hint ?? "");
    setPriming(question.priming_text ?? "");
    setSeedClusters(question.seed_clusters ?? null);
    setFormType(question.question_type ?? "open_text");
    setFormConfig(question.config ?? {});
    setConfigError(null);
    // Check if question has any responses (limit:1 for efficiency)
    getResponses(question.id, { limit: 1 }).then((rows) => {
      setHasResponses(rows.length > 0);
    }).catch(() => {
      // Fail silently — conservative: treat as no responses
      setHasResponses(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  const handleTypeChange = (next: QuestionType) => {
    setFormType(next);
    setFormConfig(defaultConfigForType(next));
    setConfigError(null);
  };

  const handleSave = async () => {
    const validationError = validateConfig(formType, formConfig);
    if (validationError) {
      setConfigError(validationError);
      return;
    }
    setConfigError(null);
    setSaving(true);
    try {
      await updateQuestion(question.id, {
        text,
        hint,
        priming_text: priming,
        seed_clusters: seedClusters,
      });
      await updateQuestionConfig(question.id, formConfig);
      await reload();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 resize-none focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors leading-[1.55]";

  return (
    <div className="flex flex-col gap-5 max-w-2xl">
      <QuestionTypePicker
        value={formType}
        onChange={handleTypeChange}
        locked={hasResponses}
        lockReason="Fragentyp ist nach erster Antwort gesperrt."
      />
      {formType === "nps" && (
        <NPSConfigForm
          value={formConfig as NPSConfig}
          onChange={setFormConfig}
        />
      )}
      {formType === "multiple_choice" && (
        <MCQConfigForm
          value={formConfig as MCQConfig}
          onChange={setFormConfig}
          locked={hasResponses}
        />
      )}
      {formType === "polar" && (
        <PolarConfigForm
          value={formConfig as PolarConfig}
          onChange={setFormConfig}
          locked={hasResponses}
        />
      )}
      {formType === "likert" && (
        <LikertConfigForm
          value={formConfig as LikertConfig}
          onChange={setFormConfig}
        />
      )}
      {configError && (
        <p className="text-sm text-rc-danger">{configError}</p>
      )}
      <FormField label="Fragetext" htmlFor="qe-text">
        <textarea id="qe-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} className={fieldClass} />
      </FormField>
      <FormField label="Hinweis (für Teilnehmer:innen)" htmlFor="qe-hint">
        <textarea
          id="qe-hint"
          value={hint}
          onChange={(e) => setHint(e.target.value)}
          rows={2}
          placeholder={'Optional. Sei konkret. Ein Beispiel ist mehr wert als ein Prinzip.'}
          className={fieldClass}
        />
      </FormField>
      <FormField
        label="KI-Kontext (privat. Steuert, wie die KI Antworten interpretiert.)"
        htmlFor="qe-priming"
      >
        <textarea
          id="qe-priming"
          value={priming}
          onChange={(e) => setPriming(e.target.value)}
          rows={5}
          placeholder="Optional. Was probt diese Frage wirklich?"
          className={fieldClass}
        />
      </FormField>
      <SeedClustersEditor value={seedClusters} onChange={setSeedClusters} />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper font-semibold text-[14px] rounded-pill px-5 py-2.5 transition-colors active:scale-[0.97]"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved ? "Gespeichert" : saving ? "Speichere…" : "Änderungen speichern"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-2 text-rc-danger hover:text-rc-paper hover:bg-rc-danger font-semibold text-[14px] rounded-pill border-[1.5px] border-rc-danger/40 px-4 py-2.5 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Frage löschen
        </button>
      </div>
    </div>
  );
}

function FormField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] block mb-2"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Reset confirm modal ──────────────────────────────────────────────────

function ResetConfirmModal({
  resetting,
  onCancel,
  onConfirm,
}: {
  resetting: boolean;
  onCancel: () => void;
  onConfirm: (mode: ResetMode) => void;
}) {
  const [mode, setMode] = useState<ResetMode>("ai");
  const optionCls = (selected: boolean) =>
    `text-left px-4 py-3 rounded-xl border-[1.5px] transition-colors ease-rc-out ${
      selected
        ? "bg-rc-berry/8 border-rc-berry text-rc-berry"
        : "bg-rc-paper border-rc-fog text-rc-charcoal hover:text-rc-black hover:border-rc-charcoal/40"
    }`;
  return (
    <ModalShell onClose={onCancel} title="Event zurücksetzen" labelledBy="reset-event-title">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-full bg-rc-berry/10 border border-rc-berry/30 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-rc-berry" />
        </div>
        <div>
          <p id="reset-event-title" className="text-rc-black font-semibold text-[17px] tracking-[-0.01em]">
            Event zurücksetzen
          </p>
          <p className="text-rc-slate text-[13px] mt-0.5">Kann nicht rückgängig gemacht werden.</p>
        </div>
      </div>
      <div role="radiogroup" aria-label="Was zurückgesetzt wird" className="grid grid-cols-2 gap-2 mb-4">
        <button type="button" role="radio" aria-checked={mode === "ai"} onClick={() => setMode("ai")} className={optionCls(mode === "ai")}>
          <span className="block text-[14px] font-semibold">Nur KI-Ergebnisse</span>
          <span className="block text-[12px] mt-0.5 opacity-80">Cluster, Themen und Insights</span>
        </button>
        <button type="button" role="radio" aria-checked={mode === "all"} onClick={() => setMode("all")} className={optionCls(mode === "all")}>
          <span className="block text-[14px] font-semibold">Alles</span>
          <span className="block text-[12px] mt-0.5 opacity-80">Auch alle Antworten</span>
        </button>
      </div>
      <p className="text-rc-charcoal text-[14px] leading-[1.6] mb-7">
        {mode === "ai" ? (
          <>
            Alle Cluster-Läufe und Insights werden gelöscht, die Fixierung wird aufgehoben.{" "}
            <strong className="font-semibold text-rc-black">Antworten, Fragen und Bühnen-Ansichten bleiben erhalten.</strong>
          </>
        ) : (
          <>
            Alle Antworten, Cluster und Insights werden dauerhaft gelöscht.{" "}
            <strong className="font-semibold text-rc-black">Fragetexte bleiben erhalten.</strong>
          </>
        )}
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={resetting}
          className="flex-1 inline-flex items-center justify-center bg-transparent border-[1.5px] border-rc-fog hover:border-rc-charcoal text-rc-black rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={() => onConfirm(mode)}
          disabled={resetting}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors active:scale-[0.97]"
        >
          {resetting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <><RotateCcw className="w-4 h-4" /> {mode === "ai" ? "KI-Ergebnisse löschen" : "Alles zurücksetzen"}</>
          )}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Analysis confirm modal ────────────────────────────────────────────────

function AnalysisConfirmModal({
  running,
  onCancel,
  onConfirm,
}: {
  running: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell onClose={onCancel} title="Auswertung starten" labelledBy="analysis-confirm-title">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-full bg-rc-berry/10 border border-rc-berry/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-rc-berry" />
        </div>
        <div>
          <p id="analysis-confirm-title" className="text-rc-black font-semibold text-[17px] tracking-[-0.01em]">
            Auswertung starten
          </p>
          <p className="text-rc-slate text-[13px] mt-0.5">Für alle Fragen mit Antworten.</p>
        </div>
      </div>
      <p className="text-rc-charcoal text-[14px] leading-[1.6] mb-7">
        Jede Frage mit Antworten wird geclustert und von den aktivierten KI-Tools analysiert.
        Die Ergebnisse werden anschließend für die Bühne eingefroren.{" "}
        Fragen lassen sich danach einzeln wieder entsperren. Zum Schluss laufen die
        Event-weiten KI-Tools und das Manifest über alle Fragen, Verteilungen und Cluster.
      </p>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={running}
          className="flex-1 inline-flex items-center justify-center bg-transparent border-[1.5px] border-rc-fog hover:border-rc-charcoal text-rc-black rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={running}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors active:scale-[0.97]"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Auswertung starten</>}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Add question modal ───────────────────────────────────────────────────

function AddQuestionModal({
  nextIndex,
  onClose,
  onCreate,
}: {
  nextIndex: number;
  onClose: () => void;
  onCreate: (text: string, hint: string, priming: string, questionType: QuestionType, questionConfig: QuestionConfig) => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [hint, setHint] = useState("");
  const [priming, setPriming] = useState("");
  const [formType, setFormType] = useState<QuestionType>("open_text");
  const [formConfig, setFormConfig] = useState<QuestionConfig>({});
  const [configError, setConfigError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleTypeChange = (next: QuestionType) => {
    setFormType(next);
    setFormConfig(defaultConfigForType(next));
    setConfigError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const validationError = validateConfig(formType, formConfig);
    if (validationError) {
      setConfigError(validationError);
      return;
    }
    setConfigError(null);
    setCreating(true);
    try {
      await onCreate(text.trim(), hint.trim(), priming.trim(), formType, formConfig);
    } finally {
      setCreating(false);
    }
  };

  const fieldClass =
    "w-full bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-4 py-3 text-[15px] text-rc-black placeholder:text-rc-slate/70 resize-none focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15 transition-colors leading-[1.55]";

  return (
    <ModalShell onClose={onClose} title="Frage hinzufügen" labelledBy="add-question-title" wide>
      <div className="mb-6">
        <p className="text-[11px] text-rc-berry font-semibold tracking-[0.14em] uppercase mb-1.5">
          Frage {nextIndex}
        </p>
        <h2 id="add-question-title" className="text-rc-black font-bold text-[20px] tracking-[-0.015em]">
          Frage hinzufügen.
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <QuestionTypePicker
          value={formType}
          onChange={handleTypeChange}
        />
        {formType === "nps" && (
          <NPSConfigForm
            value={formConfig as NPSConfig}
            onChange={setFormConfig}
          />
        )}
        {formType === "multiple_choice" && (
          <MCQConfigForm
            value={formConfig as MCQConfig}
            onChange={setFormConfig}
          />
        )}
        {formType === "polar" && (
          <PolarConfigForm
            value={formConfig as PolarConfig}
            onChange={setFormConfig}
          />
        )}
        {formType === "likert" && (
          <LikertConfigForm
            value={formConfig as LikertConfig}
            onChange={setFormConfig}
          />
        )}
        {configError && (
          <p className="text-sm text-rc-danger">{configError}</p>
        )}
        <FormField label="Frage" htmlFor="aq-text">
          <textarea
            id="aq-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            autoFocus
            rows={3}
            placeholder="Welche Frage möchtest du stellen?"
            className={fieldClass}
          />
        </FormField>
        <FormField label="Hinweis" htmlFor="aq-hint">
          <textarea
            id="aq-hint"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            rows={2}
            placeholder="Optional. Wird unter der Frage angezeigt."
            className={fieldClass}
          />
        </FormField>
        <FormField label="KI-Kontext (privat)" htmlFor="aq-priming">
          <textarea
            id="aq-priming"
            value={priming}
            onChange={(e) => setPriming(e.target.value)}
            rows={3}
            placeholder="Optional. Was probt diese Frage wirklich?"
            className={fieldClass}
          />
        </FormField>
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center bg-transparent border-[1.5px] border-rc-fog hover:border-rc-charcoal text-rc-black rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={creating || !text.trim()}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold transition-colors active:scale-[0.97]"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Frage hinzufügen"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Manifesto modal ──────────────────────────────────────────────────────

function ManifestoModal({
  eventId,
  joinCode,
  onClose,
  canApprove,
}: {
  eventId: string;
  joinCode: string;
  accessToken: string;
  onClose: () => void;
  canApprove: boolean;
}) {
  const [pending, setPending] = useState<AIInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setPending(await getPendingManifestos(eventId));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void reload(); }, [reload]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await triggerManifesto(eventId, true);
      if ("skipped" in result && result.skipped) {
        showToast("Gedrosselt — zu wenig neue Beiträge.");
      } else if ("is_approved" in result) {
        showToast(
          result.is_approved
            ? "Manifest erstellt und freigegeben."
            : "Manifest erstellt — Freigabe erforderlich.",
        );
        await reload();
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Fehler beim Erstellen.");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (insight: AIInsight) => {
    await approveManifesto(insight.id);
    showToast("Manifest freigegeben.");
    await reload();
  };

  const handleDiscard = async (insight: AIInsight) => {
    await setInsightDisplayed(insight.id, false);
    showToast("Manifest verworfen.");
    await reload();
  };

  return (
    <ModalShell onClose={onClose} title="Manifest" labelledBy="manifesto-title" wide>
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-rc-black text-rc-paper text-[13px] font-semibold rounded-pill px-5 py-2.5 shadow-hard animate-fade-up pointer-events-none">
          {toast}
        </div>
      )}
      <div className="mb-5">
        <p className="text-[11px] text-rc-berry font-semibold tracking-[0.14em] uppercase mb-1.5">
          Living Manifest
        </p>
        <h2 id="manifesto-title" className="text-rc-black font-bold text-[20px] tracking-[-0.015em]">
          Manifest verwalten.
        </h2>
      </div>

      <div className="mb-5">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="inline-flex items-center gap-2 bg-rc-berry hover:bg-rc-berry-700 disabled:bg-rc-fog disabled:text-rc-slate disabled:cursor-not-allowed text-rc-paper font-semibold text-[14px] rounded-pill px-5 py-2.5 transition-colors active:scale-[0.97]"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Manifest neu erzeugen
        </button>
        <p className="text-[12px] text-rc-slate mt-2 leading-[1.5]">
          Erzeugt ein neues Manifest aus allen aktuellen Antworten des Events.
        </p>
        <a
          href={`${window.location.origin}/insights/${joinCode}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-berry hover:text-rc-berry-700 mt-3 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Veröffentlichte Insights-Seite öffnen
        </a>
      </div>

      <div className="border-t border-rc-fog pt-5">
        <p className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] mb-3">
          Wartet auf Freigabe
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-rc-slate text-[13px] py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Lade…
          </div>
        ) : pending.length === 0 ? (
          <p className="text-[13px] text-rc-slate italic py-4">
            Keine Manifeste warten auf Freigabe.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {pending.map((insight) => (
              <div
                key={insight.id}
                className="bg-rc-mist border border-rc-fog rounded-xl p-4 flex flex-col gap-3"
              >
                <p className="text-[12px] text-rc-slate">
                  {new Date(insight.created_at).toLocaleString("de-DE", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                  {" · "}
                  {insight.responses_at_run} Antworten
                </p>
                <p className="text-[13px] text-rc-black leading-[1.55] line-clamp-4">
                  {insight.content}
                </p>
                <div className="flex gap-2">
                  {canApprove && (
                    <button
                      type="button"
                      onClick={() => handleApprove(insight)}
                      className="inline-flex items-center gap-1.5 text-[13px] font-semibold bg-rc-berry text-rc-paper rounded-pill px-4 py-1.5 hover:bg-rc-berry-700 transition-colors active:scale-[0.97]"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Freigeben
                    </button>
                  )}
                  {canApprove && (
                    <button
                      type="button"
                      onClick={() => handleDiscard(insight)}
                      className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rc-slate border border-rc-fog rounded-pill px-4 py-1.5 hover:text-rc-black hover:border-rc-charcoal/40 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Verwerfen
                    </button>
                  )}
                  {!canApprove && (
                    <p className="text-[12px] text-rc-slate italic">
                      Nur die*der Owner oder ein Co-Admin kann Manifeste freigeben.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// ─── Modal shell ──────────────────────────────────────────────────────────

function ModalShell({
  onClose,
  title,
  labelledBy,
  children,
  wide = false,
}: {
  onClose: () => void;
  title: string;
  labelledBy: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className="fixed inset-0 z-50 bg-rc-black/40 backdrop-blur-sm flex items-center justify-center px-4 py-8 animate-fade-up"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`relative bg-rc-paper border border-rc-fog rounded-rc-lg p-7 sm:p-8 w-full shadow-cardLg max-h-[calc(100vh-4rem)] overflow-y-auto ${
          wide ? "max-w-2xl" : "max-w-md"
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={`${title} schließen`}
          className="absolute top-4 right-4 p-1.5 text-rc-slate hover:text-rc-black hover:bg-rc-mist rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

// ─── Live cluster controls: freeze toggle + Außerhalb inspector ───────────

function FacilitatorLiveClusterControls({
  question,
  responses,
  eventReclusterThreshold,
  onManualRecluster,
}: {
  question: Question;
  responses: Response[];
  eventReclusterThreshold: number;
  onManualRecluster: () => Promise<void>;
}) {
  const { clusters: liveClusters } = useClusters(question.id);
  const visible = responses.filter((r) => isVisibleResponse(r));
  const catchall = liveClusters.find((c) => c.is_catchall === true) ?? null;
  // Stage Calm: "outside" responses are those assigned to the catch-all cluster.
  // Legacy runs (no is_catchall row) fall back to the old "cluster_id IS NULL"
  // semantics so nothing breaks for pre-Stage-Calm events.
  const outside = catchall
    ? visible.filter((r) => r.cluster_id === catchall.id)
    : visible.filter((r) => r.cluster_id == null);
  const [busyFreeze, setBusyFreeze] = useState(false);
  const [showInspector, setShowInspector] = useState(false);

  const handleToggleFreeze = async () => {
    setBusyFreeze(true);
    try {
      await setQuestionFrozen(question.id, !question.is_frozen);
    } finally {
      setBusyFreeze(false);
    }
  };

  const showRecluseNudge =
    !question.is_frozen && outside.length >= eventReclusterThreshold;

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleToggleFreeze}
          disabled={busyFreeze}
          className={`inline-flex items-center gap-2 rounded-pill px-4 py-2 text-[13px] font-semibold transition-colors border ${
            question.is_frozen
              ? "bg-rc-lime/15 border-rc-lime text-rc-black"
              : "bg-rc-paper border-rc-fog text-rc-slate hover:text-rc-black"
          }`}
          aria-pressed={question.is_frozen}
        >
          {question.is_frozen ? "Cluster fixiert" : "Cluster fixieren"}
        </button>
        <button
          type="button"
          onClick={() => setShowInspector((v) => !v)}
          className="text-[13px] text-rc-slate hover:text-rc-black transition-colors"
        >
          Sonstiges · {outside.length}
        </button>
      </div>
      {showRecluseNudge && (
        <div className="rounded-xl bg-rc-berry/8 border border-rc-berry/30 px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-[13px] text-rc-black">Mehrere Antworten passen nicht — neu clustern?</span>
          <button
            type="button"
            onClick={onManualRecluster}
            className="rounded-pill bg-rc-berry text-rc-paper text-[13px] font-semibold px-4 py-2 hover:bg-rc-berry-700 transition-colors"
          >
            Neu clustern
          </button>
        </div>
      )}
      {showInspector && outside.length > 0 && (
        <ul className="rounded-xl bg-rc-paper border border-rc-fog divide-y divide-rc-fog">
          {outside.slice(0, 20).map((r) => (
            <li key={r.id} className="px-4 py-2 text-[13px] text-rc-slate flex items-center justify-between gap-3">
              <span className="flex-1">{r.text}</span>
              <ClusterAssignSelect responseId={r.id} questionId={question.id} />
            </li>
          ))}
          {outside.length > 20 && (
            <li className="px-4 py-2 text-[12px] italic text-rc-slate/70">
              … und {outside.length - 20} weitere
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ClusterAssignSelect({ responseId, questionId }: { responseId: string; questionId: string }) {
  const { clusters } = useClusters(questionId);
  const [saving, setSaving] = useState(false);

  const handleAssign = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) return;
    setSaving(true);
    try {
      await assignResponseToCluster(responseId, value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      defaultValue=""
      disabled={saving || clusters.length === 0}
      onChange={handleAssign}
      className="text-[12px] bg-rc-paper border border-rc-fog rounded px-2 py-1 text-rc-black"
    >
      <option value="" disabled>Manuelle Zuordnung</option>
      {clusters.map((c) => (
        <option key={c.id} value={c.id}>{c.label}</option>
      ))}
    </select>
  );
}
